import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoginResponse } from '../signin-signup.interface';
import { ApiInterfaceService } from './api-interface.service';
import { catchError, of, switchMap } from 'rxjs';


export interface User {
  userName: string;
  phoneNumber: string;
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private apiInterfaceService: ApiInterfaceService) {}
  private platformId = inject(PLATFORM_ID);

  private userSubject = new BehaviorSubject<User>({
    userName:this.getUserName() ?? '',
    phoneNumber:this.getphoneNumber() ?? ''
  });
  user$: Observable<User> = this.userSubject.asObservable();

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  setUser(user: User) {
    this.userSubject.next(user);
  }

  getAccessToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('access_token');
    }
    return null;
  }

  setAccessToken(token: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('access_token', token);
    }
  }

  refreshToken(): Observable<any> {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const payload = {
      accessToken,
      refreshToken,
    };
    return this.apiInterfaceService.post<LoginResponse>(
      '/Auth/refresh',
      payload
    );
  }

  getRefreshToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('refresh_token');
    }
    return null;
  }

  setRefreshToken(token: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('refresh_token', token);
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      const now = Date.now() / 1000;
      const isExpired = decoded.exp < now;
      return decoded.exp < now;
    } catch {
      return true;
    }
  }

  getDecodedToken(): any | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }

  getUserName(): string | null {
    return this.getDecodedToken()?.name ?? null;
  }

  getEmail(): string | null {
    return this.getDecodedToken()?.email ?? null;
  }

  getUserId(): number | null {
    const id = this.getDecodedToken()?.userId;
    return id ? +id : null;
  }

  getUserRole(): string | null {
    return this.getDecodedToken()?.role ?? null;
  }

  getphoneNumber(): string | null {
    return this.getDecodedToken()?.phoneNumber ?? null;
  }

   refreshUserFromToken() {
    const userName = this.getUserName() ?? '';
    const phoneNumber = this.getphoneNumber() ?? '';
    this.setUser({ userName, phoneNumber });
  }

  getPhoneNumberAfterRefresh(): Observable<string | null> {
  return this.refreshToken().pipe(
    switchMap((res: any) => {
      const newToken = res?.data?.accessToken;
      const newRefresh = res?.data?.refreshToken;

      if (newToken && newRefresh) {
        this.setAccessToken(newToken);
        this.setRefreshToken(newRefresh);

        // 🔹 Use your existing helper here
        // const phoneNumber = this.getphoneNumber();
        // return of(phoneNumber);
      }

      // If refresh fails
      return of(null);
    }),
    catchError(() => {
      this.logout();
      return of(null);
    })
  );
}

}


